import { Code, ConnectError } from "@connectrpc/connect";
import { redirect, useNavigate } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import type {
  CardChargeOrder,
  MypaymentChargeOrder,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import CheckCircle from "~icons/material-symbols/check-circle";
import ProgressActivity from "~icons/material-symbols/progress-activity";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import {
  AmountOption,
  AwaitingMypaymentOrderCard,
  ChargeMethodOption,
} from "../../../features/charge/card-ui";
import { getChargeErrorMessage } from "../../../features/charge/charge-error-message";
import { showGMOPGErrorToast } from "../../../features/charge/gmopg-error-toast";
import {
  calculateOsakiChargeBonus,
  canUseOsakiChargeBonusCharge,
  getCurrentOsakiChargeBonusCampaignStatus,
  getRequiredOsakiChargeBonusPurchaseQuantity,
  osakiChargeBonusUnitPrice,
} from "../../../features/charge/osaki-charge-bonus-campaign";
import {
  fieldCss,
  labelCss,
  pageCss,
  panelCss,
  primaryButtonCss,
  secondaryButtonCss,
  sectionTitleCss,
} from "../../../features/charge/styles";
import type { WalletClient } from "../../../libs/client";
import { getGMOPGErrorInfoFromUnknown } from "../../../libs/connect";
import { formatTimestamp, getCurrentTime } from "../../../libs/date";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

type ChargeAmount = 5000 | 10000;
type ChargeQuantity = 1 | 2;
type ChargeMethod = "credit" | "mypayment";

type ChargeSearch = {
  amount?: number;
};

const parseChargeAmount = (amount: unknown): ChargeAmount =>
  Number(amount) === 10000 ? 10000 : 5000;

const parseChargeQuantity = (quantity: number): ChargeQuantity =>
  quantity === 2 ? 2 : 1;

export const Route = createFileRoute("/_authed/charge/")({
  validateSearch: (search: Record<string, unknown>): ChargeSearch => ({
    amount: parseChargeAmount(search.amount),
  }),
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.charge) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context: { client, tenant } }) => {
    const [awaitingOrder, availableCardChargeOrder] = await Promise.all([
      getAvailableAwaitingMypaymentOrder(client),
      tenant.slug === "miyagi"
        ? getAvailableCardChargeOrder(client)
        : Promise.resolve(null),
    ]);
    let osakiChargeBonusStatus: ReturnType<
      typeof getCurrentOsakiChargeBonusCampaignStatus
    > = null;
    let currentDate: Date | null = null;
    if (tenant.slug === "miyagi") {
      const [campaignStatuses, currentTime] = await Promise.all([
        client.getMyCampaignStatuses({}),
        getCurrentTime(),
      ]);
      currentDate = currentTime.date;
      osakiChargeBonusStatus = getCurrentOsakiChargeBonusCampaignStatus(
        campaignStatuses,
        currentDate,
      );
    }

    const canStartNewCharge =
      !!currentDate &&
      canUseOsakiChargeBonusCharge(osakiChargeBonusStatus, currentDate);

    if (tenant.slug === "miyagi" && !canStartNewCharge) {
      throw redirect({ to: "/" });
    }

    return {
      awaitingOrder,
      availableCardChargeOrder,
      osakiChargeBonusStatus,
    };
  },
  component: ChargeIndex,
});

function ChargeIndex() {
  const {
    awaitingOrder: initialAwaitingOrder,
    availableCardChargeOrder: initialAvailableCardChargeOrder,
    osakiChargeBonusStatus,
  } = Route.useLoaderData();
  const { client, tenant } = Route.useRouteContext();
  const navigate = useNavigate();
  const { amount: initialAmount } = Route.useSearch();
  const [awaitingOrder, setAwaitingOrder] =
    useState<MypaymentChargeOrder | null>(initialAwaitingOrder);
  const [availableCardChargeOrder, setAvailableCardChargeOrder] =
    useState<CardChargeOrder | null>(initialAvailableCardChargeOrder);
  const isOsakiChargeBonus = tenant.slug === "miyagi";
  const requiredPurchaseQuantity = getRequiredOsakiChargeBonusPurchaseQuantity(
    osakiChargeBonusStatus,
  );
  const displayChargeQuantity = parseChargeQuantity(
    requiredPurchaseQuantity || Number(awaitingOrder?.quantity ?? 1),
  );
  const [amount, setAmount] = useState<ChargeAmount>(() =>
    parseChargeAmount(initialAmount),
  );
  const [method, setMethod] = useState<ChargeMethod>("credit");
  const [loading, setLoading] = useState(false);
  const hasAwaitingOrder = !!awaitingOrder;
  const shouldBlockNewCharge = tenant.slug === "miyagi" && hasAwaitingOrder;
  const shouldBlockMypaymentByCard =
    tenant.slug === "miyagi" && !!availableCardChargeOrder;
  const shouldBlockMypayment =
    shouldBlockNewCharge || shouldBlockMypaymentByCard;
  const chargeMethodDisabledMessage = shouldBlockNewCharge
    ? t(
        "発行済みのコンビニチャージコードがあるため、新しい支払い手段は選択できません。",
      )
    : shouldBlockMypaymentByCard
      ? t(
          "3Dセキュア認証待ちのクレジットカードチャージがあるため、コンビニ払いは選択できません。",
        )
      : null;
  const awaitingAmount = awaitingOrder ? orderAmount(awaitingOrder) : 0;
  const awaitingAvailableUntilLabel = formatTimestamp(
    awaitingOrder?.barcodeAvailableUntil,
  );
  const chargeAmount = isOsakiChargeBonus
    ? calculateOsakiChargeBonus(displayChargeQuantity).price
    : amount;
  const chargeQuantity = isOsakiChargeBonus
    ? displayChargeQuantity
    : amount / osakiChargeBonusUnitPrice;

  useEffect(() => {
    let ignore = false;

    const refreshAwaitingOrder = async () => {
      const [order, cardOrder] = await Promise.all([
        getAvailableAwaitingMypaymentOrder(client),
        tenant.slug === "miyagi"
          ? getAvailableCardChargeOrder(client)
          : Promise.resolve(null),
      ]);
      if (!ignore) {
        setAwaitingOrder(order);
        setAvailableCardChargeOrder(cardOrder);
      }
    };

    void refreshAwaitingOrder();
    window.addEventListener("focus", refreshAwaitingOrder);
    window.addEventListener("pageshow", refreshAwaitingOrder);

    return () => {
      ignore = true;
      window.removeEventListener("focus", refreshAwaitingOrder);
      window.removeEventListener("pageshow", refreshAwaitingOrder);
    };
  }, [client, tenant.slug]);

  useEffect(() => {
    if (shouldBlockMypaymentByCard && method === "mypayment") {
      setMethod("credit");
    }
  }, [method, shouldBlockMypaymentByCard]);

  const selectMypayment = () => {
    setMethod("mypayment");
  };

  const confirmMypayment = () =>
    window.confirm(
      t(
        "コンビニ払いを選択しますか？\n本日中はクレジットカードでのチャージを選択できなくなります。",
      ),
    );

  const proceed = async () => {
    if (shouldBlockNewCharge) {
      toast.error(t("発行済みのコンビニチャージコードがあります"));
      return;
    }

    if (method === "credit") {
      await navigate({
        to: "/charge/credit",
        search: { amount: chargeAmount },
      });
      return;
    }

    if (shouldBlockMypayment) {
      toast.error(t("コンビニ払いは選択できません"));
      return;
    }

    const confirmed = confirmMypayment();
    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await client.createMypaymentChargeOrder({
        price: BigInt(osakiChargeBonusUnitPrice),
        quantity: BigInt(chargeQuantity),
      });
      if (!response.order) {
        toast.error(t("コンビニチャージコードを発行できませんでした"));
        return;
      }
      setAwaitingOrder(response.order);
      await navigate({ to: "/charge/mypayment" });
    } catch (error) {
      const gmoError = getGMOPGErrorInfoFromUnknown(error);
      if (gmoError) {
        showGMOPGErrorToast(gmoError);
        return;
      }
      showMypaymentError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header
        title={t("ポイントをチャージ")}
        onClickBack={() => navigate({ to: "/" })}
      />
      {isOsakiChargeBonus && <OsakiChargeBonusNotice />}
      <main className={pageCss}>
        {awaitingOrder && (
          <section
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            })}
          >
            <h2 className={sectionTitleCss}>
              {t("発行済みのコンビニチャージコード")}
            </h2>
            <AwaitingMypaymentOrderCard
              amount={awaitingAmount}
              availableUntilLabel={awaitingAvailableUntilLabel}
              onClick={() => navigate({ to: "/charge/mypayment" })}
            />
          </section>
        )}

        {isOsakiChargeBonus ? (
          <>
            <section
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              })}
            >
              <h2 className={sectionTitleCss}>{t("チャージ金額確認")}</h2>
              <OsakiChargeBonusBreakdown quantity={displayChargeQuantity} />
            </section>

            <OsakiChargeMethodPanel
              method={method}
              disabledCredit={shouldBlockNewCharge}
              disabledMypayment={shouldBlockMypayment}
              disabledMessage={chargeMethodDisabledMessage}
              onSelectCredit={() => setMethod("credit")}
              onSelectMypayment={selectMypayment}
            />
          </>
        ) : (
          <section
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            })}
          >
            <h2 className={sectionTitleCss}>{t("新しくポイントをチャージ")}</h2>
            <div className={panelCss}>
              <DemoChargeAmountSelector amount={amount} onChange={setAmount} />
              <ChargeMethodSelector
                method={method}
                disabledCredit={shouldBlockNewCharge}
                disabledMypayment={shouldBlockMypayment}
                disabledMessage={chargeMethodDisabledMessage}
                onSelectCredit={() => setMethod("credit")}
                onSelectMypayment={selectMypayment}
              />
            </div>
          </section>
        )}

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          })}
        >
          <button
            type="button"
            className={primaryButtonCss}
            disabled={
              loading ||
              shouldBlockNewCharge ||
              (method === "mypayment" && shouldBlockMypayment)
            }
            onClick={proceed}
          >
            {loading ? (
              <ProgressActivity
                className={css({ animation: "spin 1s linear infinite" })}
              />
            ) : (
              <>
                {t("次へ")}
                <ArrowForward />
              </>
            )}
          </button>
          <button
            type="button"
            className={secondaryButtonCss}
            disabled={loading}
            onClick={() => navigate({ to: "/" })}
          >
            {t("もどる")}
          </button>
        </div>
      </main>
    </>
  );
}

async function getAvailableAwaitingMypaymentOrder(client: WalletClient) {
  try {
    const response =
      await client.getAvailableAwaitingPaymentMypaymentChargeOrder({});
    return response.order ?? null;
  } catch (error) {
    if (error instanceof ConnectError && error.code === Code.NotFound) {
      return null;
    }
    throw error;
  }
}

async function getAvailableCardChargeOrder(client: WalletClient) {
  try {
    const response = await client.getAvailableCardChargeOrder({});
    return response.order ?? null;
  } catch (error) {
    if (error instanceof ConnectError && error.code === Code.NotFound) {
      return null;
    }
    throw error;
  }
}

function orderAmount(order: MypaymentChargeOrder) {
  return Number(order.price) * Number(order.quantity);
}

function DemoChargeAmountSelector({
  amount,
  onChange,
}: {
  amount: ChargeAmount;
  onChange: (amount: ChargeAmount) => void;
}) {
  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      })}
    >
      <div className={labelCss}>{t("チャージ金額入力")}</div>
      <div className={fieldCss}>
        <span
          className={css({
            flex: 1,
            minW: 0,
            textAlign: "right",
            color: "text.primary",
            fontSize: "16px",
            lineHeight: "1.5",
          })}
        >
          {formatNumber(amount)}
        </span>
        <span
          className={css({
            fontSize: "16px",
            lineHeight: "1.5",
            fontWeight: "600",
            color: "text.primary",
          })}
        >
          pt
        </span>
      </div>
      <div
        className={css({
          display: "flex",
          gap: "16px",
        })}
      >
        <AmountOption
          amount={5000}
          selected={amount === 5000}
          onClick={() => onChange(5000)}
        />
        <AmountOption
          amount={10000}
          selected={amount === 10000}
          onClick={() => onChange(10000)}
        />
      </div>
    </div>
  );
}

function ChargeMethodSelector({
  method,
  disabledCredit,
  disabledMypayment,
  disabledMessage,
  onSelectCredit,
  onSelectMypayment,
}: {
  method: ChargeMethod;
  disabledCredit: boolean;
  disabledMypayment: boolean;
  disabledMessage: string | null;
  onSelectCredit: () => void;
  onSelectMypayment: () => void;
}) {
  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      })}
    >
      <div className={labelCss}>{t("チャージ手段選択")}</div>
      <ChargeMethodOption
        selected={!disabledCredit && method === "credit"}
        title={t("クレジットカードからチャージ")}
        description={t("登録したカードからチャージします。")}
        disabled={disabledCredit}
        onClick={onSelectCredit}
      />
      <ChargeMethodOption
        selected={!disabledMypayment && method === "mypayment"}
        title={t("コンビニ払いでチャージ")}
        description={t("レジで QRコードを提示します。")}
        disabled={disabledMypayment}
        onClick={onSelectMypayment}
      />
      {disabledMessage && (
        <p
          className={css({
            color: "text.tertiary",
            fontSize: "12px",
            lineHeight: "1.4",
          })}
        >
          {disabledMessage}
        </p>
      )}
    </div>
  );
}

function OsakiChargeBonusNotice() {
  return (
    <section
      className={css({
        bg: "surface.accentSuccessLight",
        p: "16px",
        display: "flex",
        gap: "4px",
        alignItems: "flex-start",
      })}
    >
      <CheckCircle
        className={css({
          color: "text.accentSuccess",
          flexShrink: 0,
          w: "24px",
          h: "24px",
        })}
      />
      <div
        className={css({
          flex: 1,
          minW: 0,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        })}
      >
        <p
          className={css({
            color: "text.primary",
            fontSize: "16px",
            lineHeight: "1.5",
          })}
        >
          {t("おおさきポイントアップ商品券事業")}
        </p>
        <p
          className={css({
            color: "text.secondary",
            fontSize: "14px",
            lineHeight: "1.4",
          })}
        >
          {t("金額を確認しチャージ手段を選択してください。")}
        </p>
      </div>
    </section>
  );
}

function OsakiChargeMethodPanel({
  method,
  disabledCredit,
  disabledMypayment,
  disabledMessage,
  onSelectCredit,
  onSelectMypayment,
}: {
  method: ChargeMethod;
  disabledCredit: boolean;
  disabledMypayment: boolean;
  disabledMessage: string | null;
  onSelectCredit: () => void;
  onSelectMypayment: () => void;
}) {
  return (
    <section
      className={css({
        bg: "surface.primary",
        rounded: "16px",
        p: "20px",
      })}
    >
      <ChargeMethodSelector
        method={method}
        disabledCredit={disabledCredit}
        disabledMypayment={disabledMypayment}
        disabledMessage={disabledMessage}
        onSelectCredit={onSelectCredit}
        onSelectMypayment={onSelectMypayment}
      />
    </section>
  );
}

function OsakiChargeBonusBreakdown({ quantity }: { quantity: number }) {
  const campaign = calculateOsakiChargeBonus(quantity);

  return (
    <div
      className={css({
        border: "1px solid",
        borderColor: "border.primary",
        rounded: "16px",
        p: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      })}
    >
      <BreakdownRow
        label={t("チャージ金額")}
        value={campaign.price}
        unit={t("円")}
        large
      />
      <BreakdownRow
        label={t("専用券ポイント付与")}
        value={campaign.dedicatedPoint}
        unit="pt"
      />
      <BreakdownRow
        label={t("共通券ポイント付与")}
        value={campaign.commonPoint}
        unit="pt"
      />
    </div>
  );
}

function BreakdownRow({
  label,
  value,
  unit,
  large,
}: {
  label: string;
  value: number;
  unit: string;
  large?: boolean;
}) {
  return (
    <div
      className={css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
      })}
    >
      <span
        className={css({
          color: "text.primary",
          fontSize: "14px",
          lineHeight: "1.4",
        })}
      >
        {label}
      </span>
      <span
        className={css({
          display: "flex",
          alignItems: "baseline",
          gap: "4px",
          whiteSpace: "nowrap",
        })}
      >
        <span
          className={css({
            color: large ? "text.primary" : "text.secondary",
            fontSize: large ? "24px" : "16px",
            lineHeight: large ? "1.35" : "1.5",
          })}
        >
          {formatNumber(value)}
        </span>
        <span
          className={css({
            color: large ? "text.primary" : "text.secondary",
            fontSize: "14px",
            lineHeight: "1.4",
          })}
        >
          {unit}
        </span>
      </span>
    </div>
  );
}

function showMypaymentError(error: unknown) {
  toast.error(
    getChargeErrorMessage(
      error,
      t("コンビニチャージコードの発行に失敗しました"),
    ),
  );
}
